import { NavLink } from "react-router-dom";
import { FiCheck } from "react-icons/fi";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function AboutPage() {
  const sections = [
    { id: "overview", title: "Who We Are" },
    { id: "manufacturing", title: "Manufacturing" },
    { id: "experience", title: "Experience" },
    { id: "consultancy", title: "Consultancy" },
    { id: "system", title: "Our Systems" },
    { id: "mission", title: "Mission & Vision" },
    { id: "values", title: "Values & Sustainability" },
    { id: "why", title: "Why Choose H-LINE" },
    { id: "faq", title: "FAQ" },
  ];
  const [currentImage, setCurrentImage] = useState(0);
  const [activeId, setActiveId] = useState("overview");

  const AZURE_BASE_URL =
    "https://hlineweb-f6dwh8btedejg7fz.z03.azurefd.net//hlinefiles/HLineWeb/Projects/assets/projects/windows/";

  const images = [
    `${AZURE_BASE_URL}CHL00625-HDR.jpg`,
    `${AZURE_BASE_URL}CHL00701-HDR.jpg`,
    `${AZURE_BASE_URL}CHL00908-HDR.jpg`,
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-50% 0px -50% 0px",
        threshold: 0,
      }
    );

    const sectionElements = document.querySelectorAll("section[id]");
    sectionElements.forEach((section) => observer.observe(section));

    return () => {
      sectionElements.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <main className="bg-transparent text-white animate-[page-in_.35s_ease]">
      <section className="relative w-full h-[60vh] overflow-hidden flex items-center justify-start">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentImage}
            src={images[currentImage]}
            alt="About H-Line"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          />
        </AnimatePresence>

        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />

        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="relative z-10 container"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold max-w-3xl">
            About H-LINE
          </h1>
          <p className="mt-4 text-white/80 text-base sm:text-lg max-w-xl leading-relaxed">
            We engineer aluminium windows, doors and façades that elevate modern
            architecture — combining aesthetic precision with long-term
            performance.
          </p>
        </motion.div>
      </section>

      <div className="container mx-auto pb-24 grid md:grid-cols-[260px_1fr] gap-10 py-14 md:py-20">
        <aside className="md:sticky md:top-24 h-max hidden md:block">
          <nav className="space-y-2 text-sm font-medium">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`block py-1 rounded-md transition-colors duration-200 ${
                  activeId === s.id
                    ? "text-white font-semibold"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {s.title}
              </a>
            ))}
          </nav>
        </aside>

        <article className="space-y-16">
          <Section id="overview" title="Who We Are">
            <p className="text-white/80 leading-relaxed">
              Founded in <strong>2007</strong>,{" "}
              <strong>H-LINE (Harekrishna Enterprise)</strong>
              is one of India’s trusted names in architectural aluminium
              systems. We design and deliver{" "}
              <em>Windows, Doors, Façades, Railings and Louvers</em> — combining
              aesthetic precision with engineering strength.
            </p>
            <p className="mt-4 text-white/80 leading-relaxed">
              Our philosophy is simple:{" "}
              <strong>every opening tells a story</strong>. Whether it’s a
              minimalist home or a large commercial building, H-LINE products
              ensure performance, peace and long-term reliability.
            </p>
          </Section>

          <Section id="manufacturing" title="Manufacturing Excellence">
            <p className="text-white/80 leading-relaxed">
              Our <strong>15,000 sq.ft. facility</strong> in Surat, Gujarat,
              houses precision-grade cutting, milling, crimping and finishing
              equipment. Each window and door undergoes rigorous QC for
              alignment, water-tightness and surface finish.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
                <h4 className="font-semibold mb-1 text-white">
                  Quality Assurance
                </h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  From extrusion to powder-coating, every step follows ISO and
                  EN-standard quality protocols.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
                <h4 className="font-semibold mb-1 text-white">
                  Automation Ready
                </h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  CNC precision machining and barcode-based production tracking
                  ensure accuracy and consistency.
                </p>
              </div>
            </div>
          </Section>

          <Section id="experience" title="Our Experience">
            <p className="text-white/80 leading-relaxed">
              With over <strong>15+ years</strong> of experience and hundreds of
              successful façade and glazing projects, we understand the smallest
              details that make a building truly functional and timeless.
            </p>
            <p className="mt-3 text-white/80 leading-relaxed">
              Our projects range from luxury residences to corporate towers and
              institutional buildings — always blending form and performance.
            </p>
          </Section>

          <Section id="consultancy" title="Consultancy & Design Support">
            <p className="text-white/80 leading-relaxed">
              Our design team provides complete technical consultancy —
              including section optimization, wind load analysis, water
              infiltration checks, and frame design validation.
            </p>
            <p className="mt-3 text-white/80 leading-relaxed">
              We also help architects and clients visualize product aesthetics,
              using 3D renders and prototype mockups before project rollout.
            </p>
          </Section>

          <Section id="system" title="Our Systems & Solutions">
            <p className="text-white/80 leading-relaxed">
              H-LINE offers a curated range of aluminium systems — from minimal
              sliding windows to high-rise unitized façades. All products are{" "}
              <strong>custom engineered</strong> for climate, acoustics, and
              energy efficiency.
            </p>
            <ul className="mt-4 space-y-2 text-white/80">
              {[
                "Slim-frame sliding doors and windows",
                "Casement and tilt-turn systems",
                "Double-skin and unitized façades",
                "Glass railings and partitions",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-sm bg-white/15">
                    <FiCheck size={14} />
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section id="mission" title="Mission & Vision">
            <h3 className="text-xl font-bold">Our Mission</h3>
            <p className="text-white/80 leading-relaxed mt-2">
              To provide cutting-edge, sustainable and aesthetic aluminium
              systems that redefine modern architecture — built on craftsmanship
              and trust.
            </p>

            <h3 className="text-xl font-bold mt-6">Our Vision</h3>
            <p className="text-white/80 leading-relaxed mt-2">
              To be India’s most respected brand for architectural fenestration
              — delivering lasting comfort, quality and design value.
            </p>
          </Section>

          <Section id="values" title="Values & Sustainability">
            <ul className="mt-2 space-y-2 text-white/80">
              {[
                "Integrity and transparency guide every decision.",
                "Sustainability is central to all our processes.",
                "We believe innovation is the key to long-term success.",
                "People, partnerships and purpose drive our growth.",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-sm bg-white/15">
                    <FiCheck size={14} />
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section id="why" title="Why Choose H-LINE">
            <ul className="mt-2 space-y-2 text-white/80">
              {[
                "Customized systems designed for your site & climate.",
                "Marine-grade finishes and precision hardware.",
                "Certified performance for air, water & acoustic sealing.",
                "Dedicated service and post-installation support.",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-sm bg-white/15">
                    <FiCheck size={14} />
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section id="faq" title="FAQ">
            <details className="group rounded-lg border border-white/10 p-4">
              <summary className="cursor-pointer font-semibold">
                What systems do you work with?
              </summary>
              <div className="mt-2 text-white/80">
                We partner with premium Indian and global suppliers such as
                Alumil, Reynaers and Fenesta depending on project requirements.
              </div>
            </details>

            <details className="group rounded-lg border border-white/10 p-4 mt-4">
              <summary className="cursor-pointer font-semibold">
                What’s the typical project timeline?
              </summary>
              <div className="mt-2 text-white/80">
                From design approval to installation — typically 6–10 weeks,
                depending on size and complexity.
              </div>
            </details>
          </Section>

          <div className="pt-4 text-center md:text-left">
            <NavLink
              to="/contact"
              className="inline-flex px-6 py-3 rounded-md bg-white text-black font-semibold hover:bg-white/90 transition"
            >
              Start Your Project
            </NavLink>
          </div>
        </article>
      </div>
    </main>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl sm:text-3xl font-extrabold">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}
